import { Injectable, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import {
  catchError,
  endWith,
  filter,
  map,
  of,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import { BpmnEditorEvents } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.events'
import {
  AiDeveloperAPI,
  ExplainBusinessRulePayload,
  FixBusinessRuleWarningsPayload,
  GenerateBusinessRuleFromDocumentResponse,
  GenerateBusinessRulePayload,
  GenerateCustomComponentPayload,
  GenerateExpressionPayload,
  GenerateGatewayConditionPayload,
  ImproveCodePayload,
} from '@flowx/designer/modules/new-ai/agents/developer/api/developer.api'
import { showAiModal } from '@flowx/designer/modules/new-ai/common/services/ai-modal.service'
import { selectDataModelEntitiesArray } from '@flowx/designer/shared/store/selectors/data-model.selectors'

import { AiCodeEditorContextMenuActions, AiDeveloperActions } from './developer.actions'

import { FeedbackType, SendFeedbackPayload } from '../../../common/ai.dictionary'
import { AiFeedbackModalComponent } from '../../../modules/chat/components/ai-feedback-modal/ai-feedback-modal.component'
// eslint-disable-next-line import/no-cycle
import { AiActions, BusinessRuleEditorActions, NodeMenuActions } from '../../../store/ai.actions'
import { AiSelectors } from '../../../store/ai.selectors'

const DEFAULT_CUSTOM_COMPONENT_DEPS = {
  'react': '18.3.1',
  'react-dom': '18.3.1',
  '@emotion/css': '11.13.5',
  'lodash': '4.17.21',
  'axios': '1.13.2',
  'date-fns': '2.30.0',
}

@Injectable()
export class AiDeveloperEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #aiDeveloperApiService = inject(AiDeveloperAPI)
  #modalService = inject(FlxModalService)

  codeEditorImproveCodeOptionClick = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCodeEditorContextMenuActions.improveCodeOptionClick),
      map(({ selection }) => AiDeveloperActions.improveCode({ selection }))
    )
  )

  codeEditorAddCommentOptionClick = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiCodeEditorContextMenuActions.addCommentOptionClick),
      map(({ selection }) => AiDeveloperActions.addComment({ selection }))
    )
  )

  generate = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.generate),
      withLatestFrom(
        this.#store.select(AiSelectors.expressionContext),
        this.#store.select(AiSelectors.customComponentContext),
        this.#store.select(AiSelectors.hasBusinessRuleDraft),
        this.#store.select(AiSelectors.hasExpressionDraft),
        this.#store.select(AiSelectors.hasCustomComponentDraft)
      ),
      map(
        ([
          { prompt },
          expressionContext,
          customComponentContext,
          hasBusinessRuleDraft,
          hasExpressionDraft,
          hasCustomComponentDraft,
        ]) => {
          if (customComponentContext || hasCustomComponentDraft) {
            return AiDeveloperActions.generateCustomComponent({ prompt })
          }

          const hasDraftMap = {
            hasBusinessRuleDraft,
            hasExpressionDraft,
          }

          const actionEntity = Object.keys(hasDraftMap).find((key) => !!hasDraftMap[key])

          const keepDraftActionsMap = {
            hasBusinessRuleDraft: AiDeveloperActions.generateBusinessRule,
            hasExpressionDraft: AiDeveloperActions.generateExpression,
          }

          if (actionEntity) {
            return keepDraftActionsMap[actionEntity]?.({ prompt })
          } else {
            return expressionContext
              ? AiDeveloperActions.generateExpression({ prompt })
              : AiDeveloperActions.generateBusinessRule({ prompt })
          }
        }
      )
    )
  )

  generateBusinessRuleStreamCmd = createEffect(() =>
    this.#actions$.pipe(
      ofType(NodeMenuActions.generateBusinessRuleNodeMenuActionTransitionEnd),
      withLatestFrom(
        this.#store.select(AiSelectors.businessRuleContext),
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.currentPlanStep),
        this.#store.select(AiSelectors.generatedBusinessRule),
        this.#store.select(selectDataModelEntitiesArray)
      ),
      filter(
        ([, businessRuleContext, , currentPlanStep, ,]) =>
          !!businessRuleContext && !!currentPlanStep
      ),
      map(
        ([
          ,
          businessRuleContext,
          sessionId,
          currentPlanStep,
          generatedBusinessRule,
          selectDataModelEntitiesArray,
        ]) =>
          ({
            ...businessRuleContext,
            datasource: JSON.stringify(selectDataModelEntitiesArray),
            context: generatedBusinessRule?.answer || {
              code: businessRuleContext?.expression || null,
              explanation: null,
              language: null,
              test_data: null,
              errors: null,
            },
            language: businessRuleContext?.language || 'javascript',
            question: currentPlanStep.recommended_prompt.prompt,
            session_id: sessionId,
            skip_greeting: !!currentPlanStep,
          }) satisfies GenerateBusinessRulePayload
      ),
      switchMap((payload) =>
        this.#aiDeveloperApiService.getSSEGenerateBusinessRule(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiDeveloperActions.getAnswerEventStream({ response })),
          catchError(({ status }) =>
            of(
              AiActions.getAnswerFailure({
                statusCode: status,
                errorMsg: null,
              })
            )
          ),
          endWith(AiActions.getAnswerStreamCompleted())
        )
      )
    )
  )

  generateBusinessRuleStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.generateBusinessRuleStream),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.businessRuleContext),
        this.#store.select(AiSelectors.generatedBusinessRule),
        this.#store.select(selectDataModelEntitiesArray),
        this.#store.select(AiSelectors.currentPlanStep)
      ),
      map(
        ([
          { prompt },
          sessionId,
          businessRuleContext,
          generatedBusinessRule,
          selectDataModelEntitiesArray,
          currentPlanStep,
        ]) =>
          ({
            ...businessRuleContext,
            datasource: JSON.stringify(selectDataModelEntitiesArray),
            context: generatedBusinessRule?.answer || {
              code: businessRuleContext?.expression || null,
              explanation: null,
              language: null,
              test_data: null,
            },
            language: businessRuleContext?.language || 'javascript',
            question: prompt,
            session_id: sessionId,
            skip_greeting: !!currentPlanStep,
          }) satisfies GenerateBusinessRulePayload
      ),
      switchMap((payload) =>
        this.#aiDeveloperApiService.getSSEGenerateBusinessRule(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiDeveloperActions.getAnswerEventStream({ response })),
          catchError(({ status }) =>
            of(
              AiActions.getAnswerFailure({
                statusCode: status,
                errorMsg: null,
              })
            )
          ),
          endWith(AiActions.getAnswerStreamCompleted())
        )
      )
    )
  )

  generateBusinessRule = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.generateBusinessRule),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.businessRuleContext),
        this.#store.select(AiSelectors.generatedBusinessRule),
        this.#store.select(selectDataModelEntitiesArray)
      ),
      map(
        ([
          { prompt },
          sessionId,
          businessRuleContext,
          generatedBusinessRule,
          selectDataModelEntitiesArray,
        ]) =>
          ({
            ...businessRuleContext,
            datasource: JSON.stringify(selectDataModelEntitiesArray),
            context: generatedBusinessRule?.answer || {
              code: businessRuleContext.expression,
              explanation: null,
              language: null,
              test_data: null,
            },
            question: prompt,
            session_id: sessionId,
          }) satisfies GenerateBusinessRulePayload
      ),
      switchMap((payload) =>
        this.#aiDeveloperApiService.generate(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiDeveloperActions.generateBusinessRuleSuccess({ response })),
          catchError(({ status, error }) =>
            of(
              AiDeveloperActions.generateBusinessRuleFailure({
                statusCode: status,
                errorMsg: error,
              })
            )
          )
        )
      )
    )
  )

  extractBusinessRuleFromDocument = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.extractBusinessRuleFromDocument),
      switchMap(({ documentContextReference }) =>
        this.#aiDeveloperApiService
          .extractBusinessRuleFromDocumentNew(documentContextReference)
          .pipe(takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))))
      ),
      map((response: GenerateBusinessRuleFromDocumentResponse) => {
        this.#store.dispatch(
          AiDeveloperActions.extractBusinessRuleFromDocumentSuccess({ response })
        )
        return AiDeveloperActions.generateBusinessRule({ prompt: response.promptValue })
      }),
      catchError((error) =>
        of(AiDeveloperActions.extractBusinessRuleFromDocumentFailure({ error }))
      )
    )
  )

  explainBusinessRule = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.explainBusinessRule),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.businessRuleContext),
        this.#store.select(selectDataModelEntitiesArray)
      ),
      map(
        ([, sessionId, businessRuleContext, selectDataModelEntitiesArray]) =>
          ({
            datasource: JSON.stringify(selectDataModelEntitiesArray),
            session_id: sessionId,
            full_code: businessRuleContext.expression,
            code_sequence: businessRuleContext.expression,
          }) satisfies ExplainBusinessRulePayload
      ),
      switchMap((payload) =>
        this.#aiDeveloperApiService.explain(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiDeveloperActions.explainBusinessRuleSuccess({ response })),
          catchError(({ status, error }) =>
            of(
              AiDeveloperActions.explainBusinessRuleFailure({
                statusCode: status,
                errorMsg: error,
              })
            )
          )
        )
      )
    )
  )

  improveCode = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.improveCode),
      withLatestFrom(this.#store.select(selectDataModelEntitiesArray)),
      map(
        ([{ selection }, selectDataModelEntitiesArray]) =>
          ({
            code_sequence: selection.selected,
            full_code: selection.code,
            datasource: JSON.stringify(selectDataModelEntitiesArray),
            language: selection.language,
          }) satisfies ImproveCodePayload
      ),
      switchMap((payload) =>
        this.#aiDeveloperApiService.improveCode(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiDeveloperActions.improveCodeSuccess({ response })),
          catchError(({ status, error }) =>
            of(
              AiDeveloperActions.improveCodeFailure({
                statusCode: status,
                errorMsg: error,
              })
            )
          )
        )
      )
    )
  )

  continueKeepActionEvents = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.keepBusinessRuleDraftSuccess),
      withLatestFrom(
        this.#store.select(AiSelectors.businessRuleContext),
        this.#store.select(AiSelectors.isZenMode)
      ),
      filter(([, , isZenMode]) => !isZenMode),
      switchMap(([, businessRuleContext]) => {
        BpmnEditorEvents.nodeActionCloseConfigPanel()
        return this.#aiDeveloperApiService
          .getSSEActions({
            continue_editing: true,
            action_type: 'keep_business_rule',
            context: businessRuleContext,
          })
          .pipe(
            takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
            switchMap((response) => of(AiDeveloperActions.getAnswerEventStream({ response }))),
            catchError(({ status }) =>
              of(
                AiActions.getAnswerFailure({
                  statusCode: status,
                  errorMsg: null,
                })
              )
            ),
            endWith(AiActions.getAnswerStreamCompleted())
          )
      })
    )
  )

  continueDiscardActionEvents = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.discardBusinessRuleDraft),
      withLatestFrom(
        this.#store.select(AiSelectors.businessRuleContext),
        this.#store.select(AiSelectors.isZenMode)
      ),
      filter(([, , isZenMode]) => !isZenMode),
      switchMap(([, businessRuleContext]) =>
        this.#aiDeveloperApiService
          .getSSEActions({
            continue_editing: true,
            action_type: 'discard_businessRule',
            context: businessRuleContext,
          })
          .pipe(
            takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
            switchMap((response) => of(AiDeveloperActions.getAnswerEventStream({ response }))),
            catchError(({ status }) =>
              of(
                AiActions.getAnswerFailure({
                  statusCode: status,
                  errorMsg: null,
                })
              )
            ),
            endWith(AiActions.getAnswerStreamCompleted())
          )
      )
    )
  )

  addComment = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.addComment),
      withLatestFrom(this.#store.select(selectDataModelEntitiesArray)),
      map(
        ([{ selection }, selectDataModelEntitiesArray]) =>
          ({
            code_sequence: selection.selected,
            full_code: selection.code,
            datasource: JSON.stringify(selectDataModelEntitiesArray),
            language: selection.language,
          }) satisfies ImproveCodePayload
      ),
      switchMap((payload) =>
        this.#aiDeveloperApiService
          .addComment(payload)
          .pipe(takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))))
      ),
      map((response) => AiDeveloperActions.addCommentSuccess({ response })),
      catchError(({ status, error }) =>
        of(
          AiDeveloperActions.addCommentFailure({
            statusCode: status,
            errorMsg: error,
          })
        )
      )
    )
  )

  fixBusinessRuleWarnings = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.fixBusinessRuleWarnings),
      map(
        ({ payload }) =>
          ({
            datasource: [],
            full_code: payload.expression,
            code_sequence: payload.expression,
            errors: payload.errorMessages,
          }) satisfies FixBusinessRuleWarningsPayload
      ),
      switchMap((payload) =>
        this.#aiDeveloperApiService.fixWarnings(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiDeveloperActions.fixBusinessRuleWarningsSuccess({ response })),
          catchError(({ status, error }) =>
            of(
              AiDeveloperActions.fixBusinessRuleWarningsFailure({
                statusCode: status,
                errorMsg: error,
              })
            )
          )
        )
      )
    )
  )
  fixBusinessRuleError = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.fixBusinessRuleError),
      map(
        ({ payload }) =>
          ({
            datasource: [],
            full_code: payload.expression,
            code_sequence: payload.expression,
            errors: [payload.errorMessage],
          }) satisfies FixBusinessRuleWarningsPayload
      ),
      switchMap((payload) =>
        this.#aiDeveloperApiService.fixWarnings(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiDeveloperActions.fixBusinessRuleErrorSuccess({ response })),
          catchError(({ status, error }) =>
            of(
              AiDeveloperActions.fixBusinessRuleErrorFailure({
                statusCode: status,
                errorMsg: error,
              })
            )
          )
        )
      )
    )
  )

  generateExpressionEffect = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.generateExpression),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(selectDataModelEntitiesArray)
      ),
      map(
        ([{ prompt }, sessionId, selectDataModelEntitiesArray]) =>
          ({
            datasource: JSON.stringify(selectDataModelEntitiesArray),
            question: prompt,
            session_id: sessionId,
          }) satisfies GenerateExpressionPayload
      ),
      switchMap((payload) =>
        this.#aiDeveloperApiService.generateExpression(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiDeveloperActions.generateExpressionSuccess({ response })),
          catchError(({ status, error }) =>
            of(
              AiDeveloperActions.generateExpressionFailure({
                statusCode: status,
                errorMsg: error,
              })
            )
          )
        )
      )
    )
  )

  generateGatewayConditionStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.generateGatewayCondition),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(selectDataModelEntitiesArray),
        this.#store.select(AiSelectors.currentPlanStep)
      ),
      map(([{ prompt }, sessionId, dataModelEntities, currentPlanStep]) => {
        const outgoingPaths = currentPlanStep?.step_destination?.outgoing_paths ?? []
        return {
          question: prompt,
          datasource: JSON.stringify(dataModelEntities),
          session_id: sessionId,
          skip_greeting: true,
          outgoing_paths: outgoingPaths,
        } satisfies GenerateGatewayConditionPayload
      }),
      switchMap((payload) =>
        this.#aiDeveloperApiService.generateGatewayConditionStream(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiDeveloperActions.getAnswerEventStream({ response })),
          catchError(({ status }) =>
            of(
              AiActions.getAnswerFailure({
                statusCode: status,
                errorMsg: null,
              })
            )
          ),
          endWith(AiActions.getAnswerStreamCompleted())
        )
      )
    )
  )

  onKeepDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.keepDraft),
      withLatestFrom(
        this.#store.select(AiSelectors.hasBusinessRuleDraft),
        this.#store.select(AiSelectors.hasFixWarningsBusinessRuleDraft),
        this.#store.select(AiSelectors.hasFixErrorBusinessRuleDraft),
        this.#store.select(AiSelectors.hasExpressionDraft),
        this.#store.select(AiSelectors.hasImprovedCodeDraft),
        this.#store.select(AiSelectors.hasAddCommentDraft),
        this.#store.select(AiSelectors.hasGatewayConditionDraft),
        this.#store.select(AiSelectors.reusableFunctionContext),
        this.#store.select(AiSelectors.hasCustomComponentDraft)
      ),
      map(
        ([
          ,
          hasBusinessRuleDraft,
          hasFixWarningsBusinessRuleDraft,
          hasFixErrorBusinessRuleDraft,
          hasExpressionDraft,
          hasImprovedCodeDraft,
          hasAddCommentDraft,
          hasGatewayConditionDraft,
          reusableFunctionContext,
          hasCustomComponentDraft,
        ]) => {
          // On custom component pages, use the custom component-specific keep action
          if (hasCustomComponentDraft) {
            return 'hasCustomComponentDraft'
          }

          // On reusable function pages, use the function-specific keep action
          if (reusableFunctionContext && hasBusinessRuleDraft) {
            return 'hasReusableFunctionDraft'
          }

          const hasDraftMap = {
            hasBusinessRuleDraft,
            hasExpressionDraft,
            hasFixWarningsBusinessRuleDraft,
            hasFixErrorBusinessRuleDraft,
            hasImprovedCodeDraft,
            hasAddCommentDraft,
            hasGatewayConditionDraft,
          }

          return Object.keys(hasDraftMap).find((key) => !!hasDraftMap[key])
        }
      ),
      filter((actionEntity) => !!actionEntity),
      map((actionEntity) => {
        const keepDraftActionsMap = {
          hasBusinessRuleDraft: AiDeveloperActions.keepBusinessRuleDraft,
          hasExpressionDraft: AiDeveloperActions.keepExpressionDraft,
          hasFixWarningsBusinessRuleDraft: AiDeveloperActions.keepFixWarningsBusinessRuleDraft,
          hasFixErrorBusinessRuleDraft: AiDeveloperActions.keepFixErrorBusinessRuleDraft,
          hasImprovedCodeDraft: AiDeveloperActions.keepImprovedCodeDraft,
          hasAddCommentDraft: AiDeveloperActions.keepAddCommentDraft,
          hasGatewayConditionDraft: AiDeveloperActions.keepGatewayConditionDraft,
          hasReusableFunctionDraft: AiDeveloperActions.keepReusableFunctionDraft,
          hasCustomComponentDraft: AiDeveloperActions.keepCustomComponentDraft,
        }

        return keepDraftActionsMap[actionEntity]?.()
      })
    )
  )

  onDiscardDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.discardDraft, AiActions.startNewSession),
      withLatestFrom(
        this.#store.select(AiSelectors.hasBusinessRuleDraft),
        this.#store.select(AiSelectors.hasExpressionDraft),
        this.#store.select(AiSelectors.hasFixWarningsBusinessRuleDraft),
        this.#store.select(AiSelectors.hasFixErrorBusinessRuleDraft),
        this.#store.select(AiSelectors.hasImprovedCodeDraft),
        this.#store.select(AiSelectors.hasAddCommentDraft),
        this.#store.select(AiSelectors.hasGatewayConditionDraft),
        this.#store.select(AiSelectors.hasCustomComponentDraft)
      ),
      map(
        ([
          ,
          hasBusinessRuleDraft,
          hasExpressionDraft,
          hasFixWarningsBusinessRuleDraft,
          hasFixErrorBusinessRuleDraft,
          hasImprovedCodeDraft,
          hasAddCommentDraft,
          hasGatewayConditionDraft,
          hasCustomComponentDraft,
        ]) => {
          const hasDraftMap = {
            hasCustomComponentDraft,
            hasBusinessRuleDraft,
            hasExpressionDraft,
            hasFixWarningsBusinessRuleDraft,
            hasFixErrorBusinessRuleDraft,
            hasImprovedCodeDraft,
            hasAddCommentDraft,
            hasGatewayConditionDraft,
          }

          return Object.keys(hasDraftMap).find((key) => !!hasDraftMap[key])
        }
      ),
      filter((actionEntity) => !!actionEntity),
      withLatestFrom(
        this.#store.select(AiSelectors.requestId),
        this.#store.select(AiSelectors.sessionId)
      ),
      map(([actionEntity, requestId, sessionId]) => {
        const discardDraftActionMap = {
          hasBusinessRuleDraft: AiDeveloperActions.discardBusinessRuleDraft,
          hasExpressionDraft: AiDeveloperActions.discardExpressionDraft,
          hasFixWarningsBusinessRuleDraft: AiDeveloperActions.discardFixWarningsBusinessRuleDraft,
          hasFixErrorBusinessRuleDraft: AiDeveloperActions.discardFixErrorBusinessRuleDraft,
          hasImprovedCodeDraft: AiDeveloperActions.discardImprovedCodeDraft,
          hasAddCommentDraft: AiDeveloperActions.discardAddCommentDraft,
          hasGatewayConditionDraft: AiDeveloperActions.discardGatewayConditionDraft,
          hasCustomComponentDraft: AiDeveloperActions.discardCustomComponentDraft,
        }

        return actionEntity === 'hasFixWarningsBusinessRuleDraft'
          ? discardDraftActionMap['hasFixWarningsBusinessRuleDraft']({ requestId, sessionId })
          : actionEntity === 'hasFixErrorBusinessRuleDraft'
            ? discardDraftActionMap['hasFixErrorBusinessRuleDraft']({ requestId, sessionId })
            : discardDraftActionMap[actionEntity]?.()
      })
    )
  )

  discardFixWarningsBusinessRuleDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.discardFixWarningsBusinessRuleDraft),
      switchMap(({ requestId, sessionId }) => {
        const modal = showAiModal(this.#modalService, AiFeedbackModalComponent, {
          appendTo: 'body',
        })
        return modal.data$.pipe(
          take(1),
          filter(({ confirm }) => !!confirm),
          map(
            ({ reason, feedback }) =>
              ({
                sessionId,
                requestId,
                feedbackType: FeedbackType.NEGATIVE,
                message: feedback,
                reason,
              }) satisfies SendFeedbackPayload
          ),
          map((payload) => AiDeveloperActions.sendFeedback({ payload }))
        )
      })
    )
  )

  discardFixErrorsBusinessRuleDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.discardFixErrorBusinessRuleDraft),
      switchMap(({ requestId, sessionId }) => {
        const modal = showAiModal(this.#modalService, AiFeedbackModalComponent, {
          appendTo: 'body',
        })
        return modal.data$.pipe(
          take(1),
          filter(({ confirm }) => !!confirm),
          map(
            ({ reason, feedback }) =>
              ({
                sessionId,
                requestId,
                feedbackType: FeedbackType.NEGATIVE,
                message: feedback,
                reason,
              }) satisfies SendFeedbackPayload
          ),
          map((payload) => AiDeveloperActions.sendFeedback({ payload }))
        )
      })
    )
  )

  cancelFixWarnings = createEffect(() =>
    this.#actions$.pipe(
      ofType(BusinessRuleEditorActions.cancelFixWarnings),
      map(() => AiActions.cancelPendingRequest())
    )
  )

  cancelFixError = createEffect(() =>
    this.#actions$.pipe(
      ofType(BusinessRuleEditorActions.cancelFixError),
      map(() => AiActions.cancelPendingRequest())
    )
  )

  keepReusableFunctionDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.keepReusableFunctionDraft),
      map(() => AiDeveloperActions.keepReusableFunctionDraftSuccess())
    )
  )

  generateReusableFunctionStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.generateReusableFunction),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.reusableFunctionContext)
      ),
      switchMap(([{ prompt }, sessionId, functionContext]) => {
        const payload = {
          question: prompt,
          language: functionContext?.language?.toLowerCase() || 'javascript',
          session_id: sessionId ?? '',
          skip_greeting: true,
          context: functionContext?.expression ? { code: functionContext.expression } : null,
          function_metadata: {
            name: functionContext?.name || '',
            description: functionContext?.description || '',
            inputParamsType: functionContext?.inputParamsType || 'OBJECT',
            outputParamsType: functionContext?.outputParamsType || 'OBJECT',
            input_slices: functionContext?.inputSlices || [],
            output_slices: functionContext?.outputSlices || [],
          },
        }
        return this.#aiDeveloperApiService.getSSEGenerateReusableFunction(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          switchMap((response) => of(AiDeveloperActions.getAnswerEventStream({ response }))),
          catchError(({ status }) =>
            of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
          ),
          endWith(AiActions.getAnswerStreamCompleted())
        )
      })
    )
  )

  generateCustomComponentStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.generateCustomComponent),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.customComponentContext),
        this.#store.select(AiSelectors.generatedCustomComponent),
        this.#store.select(AiSelectors.currentPlanStep)
      ),
      switchMap(
        ([
          { prompt },
          sessionId,
          customComponentContext,
          generatedCustomComponent,
          currentPlanStep,
        ]) => {
          const payload: GenerateCustomComponentPayload = {
            question: prompt,
            context: generatedCustomComponent || {
              sourceCode: customComponentContext?.sourceCode || null,
              styles: customComponentContext?.styles || null,
              packageJson: customComponentContext?.packageJson || null,
            },
            session_id: sessionId ?? '',
            skip_greeting: !!currentPlanStep,
            component_metadata: {
              name: customComponentContext?.name || '',
              uniqueIdentifier: customComponentContext?.uniqueIdentifier || '',
              available_libraries: JSON.stringify(DEFAULT_CUSTOM_COMPONENT_DEPS),
              jsx_filename: customComponentContext?.uniqueIdentifier
                ? `${customComponentContext.uniqueIdentifier}.jsx`
                : 'component.jsx',
              css_filename: customComponentContext?.uniqueIdentifier
                ? `${customComponentContext.uniqueIdentifier}.css`
                : 'component.css',
            },
          }
          return this.#aiDeveloperApiService.getSSEGenerateCustomComponent(payload).pipe(
            takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
            switchMap((response) =>
              of(AiDeveloperActions.getCustomComponentAnswerEventStream({ response }))
            ),
            catchError(({ status }) =>
              of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
            ),
            endWith(AiActions.getAnswerStreamCompleted())
          )
        }
      )
    )
  )

  keepCustomComponentDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDeveloperActions.keepCustomComponentDraft),
      map(() => AiDeveloperActions.keepCustomComponentDraftSuccess())
    )
  )

  sendFeedback = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiDeveloperActions.sendFeedback),
        switchMap(({ payload }) => this.#aiDeveloperApiService.sendFeedback(payload))
      ),
    { dispatch: false }
  )
}
